import React, { useRef, useState } from 'react'

import {
  Autocomplete,
  StateParams,
  Textfield
} from '@clientbase/clientbase-library'

import { UFOptions } from 'utils'

// Compara sem acento e sem caixa: "parana", "Paraná" e "PR" valem o mesmo
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase()

export const matchStateOption = (text: string): StateParams | undefined => {
  const typed = normalize(text)
  if (!typed) return undefined

  return UFOptions.find(
    (option: StateParams) =>
      normalize(option.label) === typed || normalize(option.value) === typed
  )
}

export type StateAutocompleteProps = {
  stateValue: StateParams | null
  setStateValue: (value: StateParams | null) => void
  setStateError: (value: string | undefined) => void
  error?: string
  label?: React.ReactNode
  placeholder?: string
  disabled?: boolean
}

/**
 * SC-7273: mesmo campo de estado do AutoCompleteState da library, mas o
 * preenchimento automático do navegador (endereço salvo) vira a seleção do
 * estado em vez de deixar a lista aberta em "Selecione o estado". O Chrome
 * digita "Paraná" no campo sem foco; o MUI tratava isso como digitação e abria
 * a lista, que só fechava ao perder o foco.
 */
const StateAutocomplete = ({
  stateValue,
  setStateValue,
  setStateError,
  error,
  label = 'Estado:',
  placeholder = 'Selecione o estado',
  disabled = false
}: StateAutocompleteProps) => {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  return (
    <Autocomplete
      disabled={disabled}
      open={open}
      // Só abre com o campo em foco: o preenchimento automático escreve no
      // campo sem focar nele
      onOpen={() => {
        if (document.activeElement === inputRef.current) setOpen(true)
      }}
      onClose={() => setOpen(false)}
      onInputChange={(_, text, reason) => {
        // Só o preenchimento automático (campo sem foco) vira seleção direta.
        // Digitando, "Para" seria o Pará no meio de "Paraná": aí vale a lista.
        if (reason !== 'input') return
        if (document.activeElement === inputRef.current) return
        const match = matchStateOption(text)
        if (!match) return

        setStateValue(match)
        setStateError(undefined)
        setOpen(false)
      }}
      isOptionEqualToValue={(option, value) =>
        (option as StateParams).value === (value as StateParams).value
      }
      onChange={(_, value) => {
        const newValue = value as StateParams | null
        setStateValue(newValue)
        if (newValue) setStateError(undefined)
      }}
      options={UFOptions}
      renderInput={(params) => (
        <Textfield
          label={label}
          value={stateValue?.label}
          helperText={error}
          error={!!error}
          placeholder={placeholder}
          {...params}
          inputProps={{
            ...params.inputProps,
            ref: (node: HTMLInputElement | null) => {
              inputRef.current = node
              const muiRef = (params.inputProps as any).ref
              if (typeof muiRef === 'function') muiRef(node)
              else if (muiRef) muiRef.current = node
            }
          }}
        />
      )}
      value={stateValue}
    />
  )
}

export default StateAutocomplete
